import * as React from 'react';

import { DataTable } from '@/components/dataTable';
import { useDataTable } from '@/components/dataTable/hooks/use-data-table';
import { DataTableToolbar } from '@/components/dataTable/ToolBar';
import { useConfirmationStore } from '@/components/dialog/confirmation.store';
import { useDynamicLocale } from '@/hoslog-intl';
import { exportCsvSingleFilePapa, filterCsvDataByVisibleColumns } from '@/lib/exportCsv';
import { Pagination } from '@/react-query/context/ApiClientContextController';
import { CUSTOMER_STATUS, Customers } from '@/services/customers/customers.types';
import useCustomerMutation from '../hooks/useCustomersMutation';
import { CustomersTableActionBar } from './ActionBar';
import { getColumns } from './Column';

export function CustomersTable({
  data,
  pagination,
  isLoading,
}: {
  data: Customers[];
  pagination: Pagination;
  isLoading: boolean;
}) {
  const { t, locale } = useDynamicLocale();
  const { openConfirmation } = useConfirmationStore();
  const { mutateAsync, resetPassword, deleteCustomer } = useCustomerMutation();

  const columns = React.useMemo(
    () => getColumns(openConfirmation, mutateAsync, resetPassword, deleteCustomer, t),
    [openConfirmation, mutateAsync, resetPassword, deleteCustomer, t],
  );

  const pageCount = React.useMemo(
    () => Math.ceil((pagination.totalItems ?? 0) / (pagination.pageSize ?? 1)),
    [pagination.totalItems, pagination.pageSize],
  );
  const CUSTOMER_EXPORT_KEYS_BY_COLUMN: Record<string, readonly string[]> = {
    id: ['SN'],
    customerInfo: [t('common.page.tableColumn.customerInfo')],
    emailAddress: [t('common.page.tableColumn.emailAddress')],
    mobileNumber: [t('common.page.tableColumn.phoneNumber')],
    rewardPoints: [t('common.page.tableColumn.rewardPoints')],
    referrer: [t('common.page.tableColumn.referrer')],
    status: [t('common.page.tableColumn.status')],
    createdDate: [t('common.page.tableColumn.createdDate')],
    updatedDate: [t('common.page.tableColumn.updatedDate')],
  };
  const finalData = data?.map((item: Customers, index: number) => {
    return {
      SN: index + 1,
      [t('common.page.tableColumn.customerInfo')]: item.customerInfo,
      [t('common.page.tableColumn.emailAddress')]: item.emailAddress,
      [t('common.page.tableColumn.phoneNumber')]: item.mobileNumber,
      [t('common.page.tableColumn.rewardPoints')]: `${item.rewardPoints}円`,
      [t('common.page.tableColumn.referrer')]: item.referrer,
      [t('common.page.tableColumn.status')]:
        item.status === CUSTOMER_STATUS.ACTIVE
          ? locale === 'en'
            ? 'Active'
            : 'アクティブ'
          : locale === 'en'
            ? 'Blocked'
            : 'ブロック済み',
      [t('common.page.tableColumn.createdDate')]: Number(item.createdDate),
      [t('common.page.tableColumn.updatedDate')]: Number(item.updatedDate),
    };
  });

  function handleExport() {
    const visibleColumnIds = table.getVisibleLeafColumns().map((column) => column.id);
    const visibleData = filterCsvDataByVisibleColumns(finalData, visibleColumnIds, CUSTOMER_EXPORT_KEYS_BY_COLUMN);

    exportCsvSingleFilePapa(visibleData, {
      format: 'YYYY/MM/DD',
    });
  }
  const { table } = useDataTable({
    data,
    columns,
    pageCount: pageCount,
    initialState: {
      sorting: [{ id: 'agentId', desc: true }],
    },
    meta: {
      title: t('userManagement.customers.table.title'),
      totalPage: pagination.totalItems,
      loading: isLoading,
      actionKey: 'exportCsv',
      actionDisabled: data?.length === 0,
      action: handleExport,
    },
    getRowId: (originalRow) => String(originalRow.agentId),
    shallow: false,
    clearOnDefault: true,
  });

  return (
    <>
      <DataTable table={table}>
        <DataTableToolbar table={table} />
        <CustomersTableActionBar />
      </DataTable>
    </>
  );
}
